import React from 'react';
import { gql } from '@apollo/client';
import useReactRouter from 'use-react-router';
import { FieldArray } from 'redux-form';
import { pick } from 'lodash/fp';
import { reduxForm, useFormValue, useMutation, useQuery } from '../components';
import LoadingPage from '../components/LoadingPage';
import { Buttons, Headding, Item, Label, Lead, Panel, Text } from '../components/PrimaryPanel';
import { FileField, FormSection } from '../components/FieldWithDiff';
import { toFormValues } from '../utils';
import Button from 'jbc-front/components/Button';
import { formatValues } from '../employee_forms/Attachments';
import { CustomDescription } from '../components/QuestionDescription';
import styles from './Attachments.scss';

const ATTACHMENTS_FRAGMENT = gql`
  fragment Attachments on Profile {
    attachments {
      id
      file {
        url
        filename
      }
    }
  }
`;

const ATTACHED_FILES_FRAGMENT = gql`
  fragment AttachedFiles on Profile {
    formerJobs {
      id
      withholdingSlipImage {
        ...AttachedFile
      }
    }
    workingStudentClassification {
      id
      image {
        ...AttachedFile
      }
    }
    handicapClassification {
      id
      image {
        ...AttachedFile
      }
    }
    generalLifeInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    nursingMedicalInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    individualAnnuityInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    earthquakeInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    socialInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    premium {
      id
      smallAndMediumSizedEnterprisesImage {
        ...AttachedFile
      }
      personalPensionImage {
        ...AttachedFile
      }
      corporatePensionImage {
        ...AttachedFile
      }
      disabilityMutualAidImage {
        ...AttachedFile
      }
    }
    housingLoan {
      id
      image {
        ...AttachedFile
      }
      certificateImage {
        ...AttachedFile
      }
    }
    dependents {
      id
      type
      thisYear {
        id
        handicapImage {
          ...AttachedFile
        }
        relatedToRelativesDocument {
          ...AttachedFile
        }
        relatedToRemittanceDocument {
          ...AttachedFile
        }
        provingStudyAbroadDocument {
          ...AttachedFile
        }
      }
      nextYear {
        id
        handicapImage {
          ...AttachedFile
        }
        relatedToRelativesDocument {
          ...AttachedFile
        }
        relatedToRemittanceDocument {
          ...AttachedFile
        }
        provingStudyAbroadDocument {
          ...AttachedFile
        }
      }
    }
  }

  fragment AttachedFile on FileObject {
    url
    filename
  }
`;

const ATTACHMENTS = gql`
  query attachments {
    request {
      id
      profile {
        id
        ...Attachments
        ...AttachedFiles
      }
    }
  }
  ${ATTACHMENTS_FRAGMENT}
  ${ATTACHED_FILES_FRAGMENT}
`;

const ANSWER_ATTACHMENTS = gql`
  mutation answerAttachments($input: AnswerAttachmentsInput!) {
    answerAttachments(input: $input) {
      questions {
        id
        status
      }
      profile {
        id
        ...Attachments
        ...AttachedFiles
      }
    }
  }
  ${ATTACHMENTS_FRAGMENT}
  ${ATTACHED_FILES_FRAGMENT}
`;

const formName = 'attachments';

const AttachedFile = ({ field }) => {
  const name = useFormValue(`${field}.name`);
  return (
    <div>
      <FileField name={`${field}.files`} label={name} multiple disabled />
    </div>
  );
};

const AttachedFiles = ({ fields }) => fields.map((field, index) => <AttachedFile key={index} field={field} />);

const QuestionForm =
  (({ handleSubmit }) => {
    const attachedFiles = useFormValue('attachedFiles');
    return (
      <Panel>
        <form onSubmit={handleSubmit}>
          <Headding className={styles.primaryPanelHeader}>ファイルの添付</Headding>
          <Item>
            <Lead>提出ファイルがあれば添付してください。</Lead>
            <CustomDescription field="top" as={Text} />
            <div className="u-mt40">
              <div>
                <Label>添付済みファイル</Label>
                {attachedFiles && attachedFiles.length > 0 ? (
                  <FieldArray name="attachedFiles" component={AttachedFiles} />
                ) : (
                  <span>添付済みのファイルはありません。</span>
                )}
              </div>
              <div className="u-mt40">
                <Label>追加添付ファイル</Label>
                <FormSection name="attachments">
                  <FileField name="file" multiple />
                </FormSection>
              </div>
            </div>
            <Buttons>
              <Button primary onClick={handleSubmit}>
                次ヘ
              </Button>
            </Buttons>
          </Item>
        </form>
      </Panel>
    );
  }) |> reduxForm({ form: formName });

const Question = () => {
  const { loading, data } = useQuery(ATTACHMENTS);
  const { history } = useReactRouter();
  const [answer] = useMutation(ANSWER_ATTACHMENTS);
  if (loading) return <LoadingPage />;
  return (
    <QuestionForm
      initialValues={{
        attachments: {
          file: data.request.profile.attachments.map(attachment => ({ ...attachment.file, id: attachment.id })).map(toFormValues)
        },
        attachedFiles: makeAttachedFiles(data.request.profile)
      }}
      onSubmit={async values => {
        await answer({ variables: { input: pick('attachments', formatValues(values)) } });
        history.push('/employee_input');
      }}
    />
  );
};

const makeAttachedFiles = profile => {
  const spouse = profile.dependents.filter(dependent => dependent.type === 'Spouse');
  const otherFamilies = profile.dependents.filter(dependent => dependent.type === 'OtherFamily');

  return [
    { name: '源泉徴収票', files: profile.formerJobs.map(job => job.withholdingSlipImage) },
    { name: '学生手帳画像', files: [profile.workingStudentClassification?.image] },
    { name: '障害者手帳画像', files: [profile.handicapClassification?.image] },
    { name: '一般の生命保険 証明書画像', files: profile.generalLifeInsurances.map(insurance => insurance.image) },
    { name: '介護医療保険 証明書画像', files: profile.nursingMedicalInsurances.map(insurance => insurance.image) },
    { name: '個人年金保険 証明書画像', files: profile.individualAnnuityInsurances.map(insurance => insurance.image) },
    { name: '地震保険 証明書画像', files: profile.earthquakeInsurances.map(insurance => insurance.image) },
    { name: '社会保険 証明書画像', files: profile.socialInsurances.map(insurance => insurance.image) },
    {
      name: '独立行政法人中小企業基盤整備機構の共済契約の掛金 証明書画像',
      files: [profile.premium?.smallAndMediumSizedEnterprisesImage]
    },
    { name: '確定拠出年金法に規定する個人型年金加入者掛金 証明書画像', files: [profile.premium?.personalPensionImage] },
    {
      name: '確定拠出年金法に規定する企業型年金加入者掛金 証明書画像',
      files: [profile.premium?.corporatePensionImage]
    },
    { name: '心身障害者扶養共済制度に関する契約の掛金 証明書画像', files: [profile.premium?.disabilityMutualAidImage] },
    { name: '住宅借入金等特別控除申告書の画像', files: [profile.housingLoan?.image] },
    { name: '住宅ローンの年末残高証明書の画像', files: [profile.housingLoan?.certificateImage] },
    {
      name: '配偶者・障害者手帳画像',
      files: [spouse[0]?.thisYear?.handicapImage, spouse[0]?.nextYear?.handicapImage]
    },
    {
      name: '被扶養者・障害者手帳画像',
      files: [...otherFamilies.map(otherFamily => [otherFamily.thisYear?.handicapImage, otherFamily.nextYear?.handicapImage]).flat()]
    },
    {
      name: '被扶養者・親族関係書類',
      files: [
        ...otherFamilies
          .map(otherFamily => [otherFamily.thisYear?.relatedToRelativesDocument, otherFamily.nextYear?.relatedToRelativesDocument])
          .flat()
      ]
    },
    {
      name: '被扶養者・送金関係書類',
      files: [
        ...otherFamilies
          .map(otherFamily => [otherFamily.thisYear?.relatedToRemittanceDocument, otherFamily.nextYear?.relatedToRemittanceDocument])
          .flat()
      ]
    },
    {
      name: '被扶養者・留学証明書類',
      files: [
        ...otherFamilies
          .map(otherFamily => [otherFamily.thisYear?.provingStudyAbroadDocument, otherFamily.nextYear?.provingStudyAbroadDocument])
          .flat()
      ]
    },
    {
      name: '配偶者・親族関係書類',
      files: [spouse[0]?.thisYear?.relatedToRelativesDocument, spouse[0]?.nextYear?.relatedToRelativesDocument]
    },
    {
      name: '配偶者・送金関係書類',
      files: [spouse[0]?.thisYear?.relatedToRemittanceDocument, spouse[0]?.nextYear?.relatedToRemittanceDocument]
    }
  ]
    .map(field => ({ ...field, files: field.files.filter(v => v?.url) }))
    .filter(field => field.files.length !== 0);
};

export default Question;
